import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, MousePointerClick, Link2, ShoppingBag, Gauge, MonitorPlay, GraduationCap, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  equipement: { label: "Équipement", icon: ShoppingBag },
  performance: { label: "Performance", icon: Gauge },
  logiciel: { label: "Logiciel", icon: MonitorPlay },
  formation: { label: "Formation", icon: GraduationCap },
};

const empty = {
  nom: "", categorie: "equipement", description: "", url: "", image: "",
  badge: "", note_interne: "", mis_en_avant: false, actif: true, ordre: 0,
};

export default function AdminAffiliates() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/affiliates`, { withCredentials: true }).then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const totalClics = items.reduce((s, a) => s + (a.clics || 0), 0);

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image téléversée");
    } catch {
      toast.error("Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.nom.trim() || !form.url.trim()) {
      toast.error("Nom et URL obligatoires");
      return;
    }
    try {
      const payload = { ...form, badge: form.badge || null, image: form.image || null, note_interne: form.note_interne || null, ordre: Number(form.ordre) || 0 };
      if (editId) {
        await axios.put(`${API}/admin/affiliates/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/affiliates`, payload, { withCredentials: true });
      }
      toast.success(editId ? "Lien mis à jour" : "Lien ajouté");
      setForm(null);
      setEditId(null);
      load();
    } catch {
      toast.error("Erreur d'enregistrement");
    }
  };

  const toggleActif = async (a) => {
    try {
      await axios.put(`${API}/admin/affiliates/${a.id}`, {
        nom: a.nom, categorie: a.categorie, description: a.description, url: a.url,
        image: a.image, badge: a.badge, note_interne: a.note_interne,
        mis_en_avant: a.mis_en_avant, actif: !a.actif, ordre: a.ordre,
      }, { withCredentials: true });
      load();
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer « ${a.nom} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/affiliates/${a.id}`, { withCredentials: true });
      toast.success("Supprimé");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const startEdit = (a) => {
    setEditId(a.id);
    setForm({ nom: a.nom, categorie: a.categorie, description: a.description || "", url: a.url, image: a.image || "", badge: a.badge || "", note_interne: a.note_interne || "", mis_en_avant: !!a.mis_en_avant, actif: !!a.actif, ordre: a.ordre || 0 });
  };

  return (
    <div data-testid="admin-affiliates" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl uppercase font-bold">Affiliation ({items.length} liens)</h2>
          <p className="text-xs text-[#71717A] mt-1">
            Gérez les liens partenaires affichés sur la page Ressources, l'accueil et sous les actualités.
          </p>
        </div>
        <button
          data-testid="affiliate-add-button"
          onClick={() => { setEditId(null); setForm({ ...empty }); }}
          className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors"
        >
          <Plus size={14} /> Ajouter un lien
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="affiliate-stat-clics">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Clics totaux</p>
            <MousePointerClick size={16} className="text-[#C5A059]" />
          </div>
          <p className="font-display text-3xl font-bold mt-3">{totalClics}</p>
        </div>
        <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="affiliate-stat-actifs">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Liens actifs</p>
            <Link2 size={16} className="text-[#C5A059]" />
          </div>
          <p className="font-display text-3xl font-bold mt-3">{items.filter((a) => a.actif).length}</p>
        </div>
      </div>

      {form && (
        <div className="border border-[#C5A059]/40 bg-[#121214] p-8" data-testid="affiliate-form">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display uppercase font-bold">{editId ? "Modifier le lien" : "Nouveau lien partenaire"}</h3>
            <button onClick={() => { setForm(null); setEditId(null); }} className="text-[#71717A] hover:text-[#C5A059]" data-testid="affiliate-form-close"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom du produit / plateforme *" data-testid="affiliate-form-nom"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} data-testid="affiliate-form-categorie"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL (votre lien affilié) *" data-testid="affiliate-form-url"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] sm:col-span-2" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description publique" rows={2} data-testid="affiliate-form-description"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] sm:col-span-2" />
            <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Badge (ex : Pro, Gratuit, Essentiel)" data-testid="affiliate-form-badge"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input type="number" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} placeholder="Ordre d'affichage" data-testid="affiliate-form-ordre"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.note_interne} onChange={(e) => setForm({ ...form, note_interne: e.target.value })} placeholder="Note interne (jamais affichée au public)" data-testid="affiliate-form-note"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] sm:col-span-2" />
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-4">
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
              <input type="checkbox" checked={form.mis_en_avant} onChange={(e) => setForm({ ...form, mis_en_avant: e.target.checked })} data-testid="affiliate-form-avant" className="accent-[#C5A059]" />
              Mis en avant (page d'accueil)
            </label>
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
              <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} data-testid="affiliate-form-actif" className="accent-[#C5A059]" />
              Actif (visible publiquement)
            </label>
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadImage} className="hidden" data-testid="affiliate-image-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="affiliate-image-button"
                className="flex items-center gap-2 border border-[#27272A] px-4 py-2.5 text-xs uppercase tracking-widest text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                <Upload size={13} /> {uploading ? "Envoi…" : form.image ? "Changer l'image" : "Image (optionnel)"}
              </button>
              {form.image && <img src={form.image} alt="Aperçu" className="h-10 w-10 object-cover border border-[#27272A]" data-testid="affiliate-image-preview" />}
            </div>
          </div>
          <button onClick={save} data-testid="affiliate-form-save"
            className="mt-6 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            {editId ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-[#27272A]">
        <table className="w-full text-sm min-w-[900px]" data-testid="affiliates-table">
          <thead>
            <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
              <th className="px-4 py-3 font-medium">Nom</th>
              <th className="px-4 py-3 font-medium">Catégorie</th>
              <th className="px-4 py-3 font-medium">URL</th>
              <th className="px-4 py-3 font-medium">Clics</th>
              <th className="px-4 py-3 font-medium">Accueil</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#71717A]">Aucun lien partenaire.</td></tr>
            )}
            {items.map((a) => {
              const cat = CATEGORIES[a.categorie] || CATEGORIES.equipement;
              return (
                <tr key={a.id} data-testid={`affiliate-row-${a.id.slice(0, 8)}`} className="border-t border-[#27272A] hover:bg-[#121214] transition-colors">
                  <td className="px-4 py-3 font-medium">
                    {a.nom}
                    {a.note_interne && <span className="block text-[10px] text-[#71717A] font-normal mt-0.5">{a.note_interne}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-xs text-[#A1A1AA]"><cat.icon size={13} className="text-[#C5A059]" /> {cat.label}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#71717A] max-w-[220px] truncate">{a.url}</td>
                  <td className="px-4 py-3 font-semibold text-[#C5A059]" data-testid={`affiliate-clics-${a.id.slice(0, 8)}`}>{a.clics || 0}</td>
                  <td className="px-4 py-3 text-xs">{a.mis_en_avant ? <span className="text-[#C5A059]">Oui</span> : <span className="text-[#3f3f46]">—</span>}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActif(a)} data-testid={`affiliate-toggle-${a.id.slice(0, 8)}`}
                      className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${a.actif ? "border-emerald-500/60 text-emerald-400" : "border-[#3f3f46] text-[#71717A]"}`}>
                      {a.actif ? "Actif" : "Inactif"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(a)} data-testid={`affiliate-edit-${a.id.slice(0, 8)}`} className="text-[#A1A1AA] hover:text-[#C5A059] transition-colors" aria-label="Modifier">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => remove(a)} data-testid={`affiliate-delete-${a.id.slice(0, 8)}`} className="text-[#A1A1AA] hover:text-red-400 transition-colors" aria-label="Supprimer">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
